import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { joinClass } from '../../lib/zoom-join';
import { minutesUntilStart } from '../../lib/session-time';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Calendar, Clock, Users, Video, Check, X, ChevronLeft, ChevronRight, AlertTriangle, Loader2, FileText, ChevronRight as ChevRight } from 'lucide-react';
import { formatDate, formatTimeLocal, formatLocalDate, formatLocalWeekdayLong, getLocalDayMonth, getLocalWeekday, toLocalDateKey, todayLocal, getInitials, getDurationLabel, getCourseTypeLabel, toISTDateStr } from '../../lib/utils';
import { toast } from 'sonner';
import TimezoneHeader from '../../components/TimezoneHeader';
import SessionDetailModal from '../../components/SessionDetailModal';
import PregnancyOnboardingModal from '../../components/PregnancyOnboardingModal';
import { isEliteTier } from '../../components/TierBadge';

export default function BookClasses() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [myBookings, setMyBookings] = useState([]);          // upcoming
  const [pastBookings, setPastBookings] = useState([]);       // past
  const [consultCalls, setConsultCalls] = useState([]);       // V66 — scheduled dietician video calls
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get('tab') === 'my-bookings' ? 'my-bookings' : 'available');
  const [bookingsTab, setBookingsTab] = useState(searchParams.get('sub') === 'past' ? 'past' : 'upcoming'); // upcoming | past
  const [dateOffset, setDateOffset] = useState(0);
  const [bufferSettings, setBufferSettings] = useState({ learner_join_buffer: 5 });
  const [cancelPreview, setCancelPreview] = useState(null); // {sessionId, preview}
  const [cancelBusy, setCancelBusy] = useState(false);
  const [detailSession, setDetailSession] = useState(null);
  const [eddModalOpen, setEddModalOpen] = useState(false);

  // EDD mandatory for Elite users — show popup if EDD is missing
  const isElite = isEliteTier(user?.learner_type);
  const pp = user?.pregnancy_profile || {};
  const stage = pp.stage || pp.course_type || '';
  const needsEdd = isElite && !pp.edd && stage !== 'postnatal';

  useEffect(() => {
    if (needsEdd) setEddModalOpen(true);
  }, [needsEdd]);

  // V27 — Free / SoulAI-paid prenatal learners book a trial class instead
  const navigateTrial = useNavigate();
  useEffect(() => {
    if (user && !isElite && stage !== 'postnatal') navigateTrial('/learner/trial-classes', { replace: true });
  }, [user, isElite, stage, navigateTrial]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const from = new Date(now.getTime() + dateOffset * 24 * 60 * 60 * 1000);
      const to = new Date(from.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const [availRes, upcomingRes, pastRes] = await Promise.all([
        api.get('/api/sessions/available', { params: { date_from: toISTDateStr(from), date_to: toISTDateStr(to) } }),
        api.get('/api/sessions/my-bookings', { params: { tab: 'upcoming' } }),
        api.get('/api/sessions/my-bookings', { params: { tab: 'past' } }),
      ]);
      setSessions(availRes.data.sessions || []);
      setMyBookings(upcomingRes.data.bookings || []);
      setConsultCalls(upcomingRes.data.consult_calls || []);
      setPastBookings(pastRes.data.bookings || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [dateOffset]);

  useEffect(() => {
    api.get('/api/settings/buffers').then(res => setBufferSettings(res.data)).catch(() => {});
  }, []);

  const handleBook = async (sessionId) => {
    if (needsEdd) {
      setEddModalOpen(true);
      return;
    }
    try {
      const res = await api.post(`/api/sessions/${sessionId}/book`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    }
  };

  const handleUnbook = async (sessionId) => {
    // Step 1: preview cancellation impact
    try {
      const res = await api.get(`/api/sessions/${sessionId}/unbook-preview`);
      setCancelPreview({ sessionId, ...res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel preview failed');
    }
  };

  const confirmUnbook = async () => {
    if (!cancelPreview) return;
    setCancelBusy(true);
    try {
      const res = await api.post(`/api/sessions/${cancelPreview.sessionId}/unbook`);
      toast.success(res.data.message || 'Booking cancelled');
      setCancelPreview(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelBusy(false);
    }
  };

  // Group available sessions by the LEARNER'S LOCAL date so a CDT learner
  // sees an IST 8:00 AM class on the previous local day (it's actually
  // 9:30 PM the night before for them). V14.5 Part 11 fix — before this,
  // grouping was by raw IST `scheduled_date` which mis-rendered every
  // class near midnight IST by one calendar day for non-IST learners.
  const sessionsByDate = {};
  sessions.forEach(s => {
    const key = toLocalDateKey(s.scheduled_date, s.scheduled_time);
    if (!sessionsByDate[key]) sessionsByDate[key] = [];
    sessionsByDate[key].push(s);
  });

  const dateRange = () => {
    const now = new Date();
    const from = new Date(now.getTime() + dateOffset * 24 * 60 * 60 * 1000);
    const to = new Date(from.getTime() + 6 * 24 * 60 * 60 * 1000);
    return `${from.toLocaleDateString('en', { month: 'short', day: 'numeric' })} - ${to.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // ─── helper: pretty duration label that prefers actual over scheduled ───
  const durationLabelFor = (s) => {
    if (s.actual_duration_minutes) {
      return `${getDurationLabel(s.actual_duration_minutes)} actual`;
    }
    return getDurationLabel(s.duration_minutes);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Book Classes</h1>
          <p className="text-muted-foreground mt-1">Choose your preferred class time</p>
        </div>
        <TimezoneHeader />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="available" data-testid="bookings-available-tab">Available Slots</TabsTrigger>
          <TabsTrigger value="my-bookings" data-testid="bookings-mine-tab">My Bookings <Badge variant="secondary" className="ml-1 text-[10px]">{myBookings.length + pastBookings.length}</Badge></TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'available' && (
        <>
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setDateOffset(Math.max(0, dateOffset - 7))} disabled={dateOffset === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">{dateRange()}</span>
            <Button variant="ghost" size="icon" onClick={() => setDateOffset(dateOffset + 7)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <Card key={i} className="rounded-2xl animate-pulse"><CardContent className="h-24 pt-6" /></Card>)}</div>
          ) : Object.keys(sessionsByDate).length === 0 ? (
            <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12">
              <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No available classes in this period</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(sessionsByDate).sort(([a],[b]) => a.localeCompare(b)).map(([date, dateSessions]) => {
                // `date` is now a LOCAL YYYY-MM-DD (V14.5 Part 11). The
                // section header reads off that local date directly.
                const isToday = date === todayLocal();
                const firstSession = dateSessions[0];
                const headingText = formatLocalWeekdayLong(firstSession.scheduled_date, firstSession.scheduled_time) || new Date(date + 'T00:00:00').toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long' });
                return (
                  <div key={date}>
                    <div className={`flex items-center gap-2 mb-3 ${isToday ? 'text-primary' : ''}`}>
                      <p className="text-sm font-semibold">{headingText}</p>
                      {isToday && <Badge variant="secondary" className="text-xs">Today</Badge>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {dateSessions.map(s => {
                        const capacityPct = (s.booked_count / s.max_capacity) * 100;
                        const almostFull = capacityPct >= 75;
                        return (
                          <Card key={s.id} className={`rounded-2xl transition-shadow hover:shadow-md ${s.is_booked_by_me ? 'ring-2 ring-primary' : ''}`}>
                            <CardContent className="pt-5 pb-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="text-base font-semibold">{formatTimeLocal(s.scheduled_time, s.scheduled_date)}</p>
                                  <p className="text-xs text-muted-foreground">{getDurationLabel(s.duration_minutes)}</p>
                                </div>
                                <Badge variant="secondary">{getCourseTypeLabel(s.course_type)}</Badge>
                              </div>
                              <p className="text-sm font-medium">{s.batch_name}</p>
                              {s.instructor_name && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <Avatar className="h-5 w-5"><AvatarFallback className="text-[8px] bg-primary/10 text-primary">{getInitials(s.instructor_name)}</AvatarFallback></Avatar>
                                  <span className="text-xs text-muted-foreground">{s.instructor_name}</span>
                                </div>
                              )}

                              {/* Capacity bar */}
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className={`flex items-center gap-1 ${almostFull ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                                    <Users className="h-3 w-3" />{s.booked_count}/{s.max_capacity} booked
                                  </span>
                                  {s.is_full ? (
                                    <span className="text-rose-600 font-medium">Full{s.waitlist_count > 0 ? ` · ${s.waitlist_count} waitlisted` : ''}</span>
                                  ) : (
                                    <span className="text-emerald-600">{s.spots_left} {s.spots_left === 1 ? 'spot' : 'spots'} left</span>
                                  )}
                                </div>
                                <div className="w-full bg-muted rounded-full h-1.5">
                                  <div className={`h-1.5 rounded-full transition-all ${s.is_full ? 'bg-rose-500' : almostFull ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${Math.min(100, capacityPct)}%` }} />
                                </div>
                              </div>

                              {/* Action */}
                              <div className="mt-3">
                                {s.is_booked_by_me ? (
                                  <div className="space-y-2">
                                    {(() => {
                                      const minsUntil = minutesUntilStart(s);
                                      const canJoinNow = s.status === 'live' || (minsUntil !== null && minsUntil <= (bufferSettings.learner_join_buffer || 5));
                                      if (canJoinNow && (s.zoom_join_url || s.status === 'live')) {
                                        return (
                                          <Button className="w-full" size="sm" onClick={() => joinClass(s.id, s.zoom_join_url)} data-testid={`available-slot-join-${s.id}`}>
                                            <Video className="h-3 w-3 mr-1" />Join Now
                                          </Button>
                                        );
                                      }
                                      return null;
                                    })()}
                                    <div className="flex items-center justify-between">
                                      <Badge className="bg-primary/10 text-primary border-0"><Check className="h-3 w-3 mr-1" />Booked</Badge>
                                      <Button variant="ghost" size="sm" className="text-destructive text-xs" onClick={() => handleUnbook(s.id)}>Drop Out</Button>
                                    </div>
                                  </div>
                                ) : s.is_waitlisted_by_me ? (
                                  <div className="flex items-center justify-between">
                                    <Badge className="bg-amber-100 text-amber-800 border-0">Waitlisted</Badge>
                                    <Button variant="ghost" size="sm" className="text-destructive text-xs" onClick={() => handleUnbook(s.id)}>Leave waitlist</Button>
                                  </div>
                                ) : s.can_book && !s.is_full ? (
                                  <Button className="w-full" size="sm" onClick={() => handleBook(s.id)} data-testid={`book-slot-${s.id}`}>
                                    {s.is_live_now ? 'Book & Join Live' : 'Book This Slot'}
                                  </Button>
                                ) : s.can_book && s.is_full ? (
                                  <Button className="w-full" size="sm" variant="outline" onClick={() => handleBook(s.id)}>Join Waitlist</Button>
                                ) : s.has_booking_on_date ? (
                                  <p className="text-xs text-muted-foreground text-center" data-testid={`already-booked-${s.id}`}>You already have a booking in this batch on this date</p>
                                ) : s.daily_cap_reached ? (
                                  // V14.5 Part 17e — Daily cap of 2 hit. Surface the reason
                                  // instead of a dead "Not Available" so the learner knows
                                  // they need to free a slot to book this class.
                                  <div className="text-center py-1.5 px-2 rounded-lg bg-rose-50 border border-rose-200" data-testid={`daily-cap-${s.id}`}>
                                    <p className="text-[11px] text-rose-900 font-medium">📅 Daily limit reached — you have {s.daily_count_for_date} bookings on this date (max 2)</p>
                                  </div>
                                ) : s.booking_closed_reason ? (
                                  // V14.3 — friendly cutoff explanation instead of a dead "Not Available" button
                                  <div className="text-center py-1.5 px-2 rounded-lg bg-amber-50 border border-amber-200" data-testid={`booking-closed-${s.id}`}>
                                    <p className="text-[11px] text-amber-900 font-medium">⏰ {s.booking_closed_reason}</p>
                                  </div>
                                ) : (
                                  <Button className="w-full" size="sm" disabled variant="outline">Not Available</Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'my-bookings' && (
        <div className="space-y-4">
          {/* V13.9 — Upcoming / Past sub-tabs */}
          <Tabs value={bookingsTab} onValueChange={setBookingsTab}>
            <TabsList>
              <TabsTrigger value="upcoming" data-testid="my-bookings-upcoming-tab">
                Upcoming <Badge variant="secondary" className="ml-1 text-[10px]">{myBookings.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="past" data-testid="my-bookings-past-tab">
                Past <Badge variant="secondary" className="ml-1 text-[10px]">{pastBookings.length}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {bookingsTab === 'upcoming' && (
            <div className="space-y-3">
              {/* V66 — scheduled video consultation with the nutritionist sits with her bookings */}
              {consultCalls.map(c => (
                <Card key={c.id} className="rounded-2xl border-sky-200 bg-sky-50/60" data-testid={`upcoming-consult-call-${c.id}`}>
                  <CardContent className="pt-4 pb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0"><Video className="h-5 w-5 text-sky-700" /></div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{c.title}</p>
                        <p className="text-xs text-sky-800">{new Date(c.scheduled_at).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })} · {c.duration_minutes} min</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => navigateTrial('/learner/nutritionist')}>Open chat</Button>
                      <a href={c.join_url} target="_blank" rel="noopener noreferrer"><Button size="sm" className="bg-sky-600 hover:bg-sky-700"><Video className="h-3.5 w-3.5 mr-1" />Join</Button></a>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {myBookings.length === 0 && consultCalls.length === 0 ? (
                <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12">
                  <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No upcoming bookings</p>
                  <Button variant="outline" className="mt-3" onClick={() => setTab('available')}>Browse Available Classes</Button>
                </CardContent></Card>
              ) : (
                myBookings.map(s => {
                  const minsUntil = minutesUntilStart(s);
                  const canJoin = s.status === 'live' || (minsUntil !== null && minsUntil <= (bufferSettings.learner_join_buffer || 5));
                  const isLiveNow = s.status === 'live';
                  return (
                    <Card
                      key={s.id}
                      className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setDetailSession(s)}
                      data-testid={`upcoming-booking-card-${s.id}`}
                    >
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-center min-w-[44px] bg-primary/10 rounded-xl px-2 py-1.5">
                              {(() => {
                                const dm = getLocalDayMonth(s.scheduled_date, s.scheduled_time);
                                return (<>
                                  <p className="text-[10px] text-primary uppercase">{dm.month}</p>
                                  <p className="text-xl font-semibold text-primary leading-tight">{dm.day}</p>
                                </>);
                              })()}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-semibold">{s.batch_name}</p>
                                {isLiveNow && (
                                  <Badge className="bg-rose-600 text-white border-0 text-[9px]">
                                    <span className="h-1 w-1 rounded-full bg-white animate-pulse mr-1 inline-block" />Live now
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{getLocalWeekday(s.scheduled_date, s.scheduled_time)} {formatTimeLocal(s.scheduled_time, s.scheduled_date)} · {getDurationLabel(s.duration_minutes)}</p>
                              {s.instructor_name && <p className="text-xs text-muted-foreground">with {s.instructor_name}</p>}
                              <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground"><Users className="h-3 w-3" />{s.booked_count} booked</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            {canJoin && s.zoom_join_url ? (
                              <Button size="sm" onClick={() => joinClass(s.id, s.zoom_join_url)} data-testid={`join-booking-${s.id}`}><Video className="h-3 w-3 mr-1" />Join</Button>
                            ) : canJoin && !s.zoom_join_url ? (
                              <Button size="sm" disabled variant="outline"><Clock className="h-3 w-3 mr-1" />Link soon</Button>
                            ) : null}
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleUnbook(s.id)}><X className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {bookingsTab === 'past' && (
            <div className="space-y-3">
              {pastBookings.length === 0 ? (
                <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12">
                  <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No past bookings yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Sessions you've attended in the last 90 days appear here.</p>
                </CardContent></Card>
              ) : (
                pastBookings.map(s => (
                  <Card
                    key={s.id}
                    className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setDetailSession(s)}
                    data-testid={`past-booking-card-${s.id}`}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-center min-w-[44px] bg-muted rounded-xl px-2 py-1.5">
                            {(() => {
                              const dm = getLocalDayMonth(s.scheduled_date, s.scheduled_time);
                              return (<>
                                <p className="text-[10px] uppercase text-muted-foreground">{dm.month}</p>
                                <p className="text-xl font-semibold leading-tight">{dm.day}</p>
                              </>);
                            })()}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-semibold">{s.batch_name}</p>
                              <Badge variant="outline" className="text-[9px] capitalize">{s.status}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {getLocalWeekday(s.scheduled_date, s.scheduled_time)} {formatTimeLocal(s.scheduled_time, s.scheduled_date)} · {durationLabelFor(s)}
                            </p>
                            {s.instructor_name && <p className="text-xs text-muted-foreground">with {s.instructor_name}</p>}
                            <div className="flex items-center gap-2 mt-1">
                              {s.has_recording && (
                                <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <Video className="h-2.5 w-2.5 mr-1" />Recording
                                </Badge>
                              )}
                              {s.has_ai_summary && (
                                <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                                  <FileText className="h-2.5 w-2.5 mr-1" />AI Summary
                                </Badge>
                              )}
                              {Array.isArray(s.topics_covered) && s.topics_covered.length > 0 && (
                                <Badge variant="outline" className="text-[9px]">{s.topics_covered.length} topics</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <ChevRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Cancel preview modal */}
      <Dialog open={!!cancelPreview} onOpenChange={(v) => !v && !cancelBusy && setCancelPreview(null)}>
        <DialogContent data-testid="unbook-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${cancelPreview?.within_window ? 'text-red-500' : 'text-amber-500'}`} />
              Cancel this booking?
            </DialogTitle>
            <DialogDescription>{cancelPreview?.message}</DialogDescription>
          </DialogHeader>
          {cancelPreview && (
            <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
              <div>Type: <strong className="capitalize">{cancelPreview.batch_type?.replace('_', ' ')}</strong></div>
              <div>Time until class: <strong>{cancelPreview.hours_until >= 0 ? `${cancelPreview.hours_until}h` : 'already started'}</strong></div>
              {cancelPreview.window_hours > 0 && (
                <div>Policy window: <strong>{cancelPreview.window_hours}h before class</strong></div>
              )}
              {cancelPreview.will_lose_credit && (
                <div className="text-red-600 font-medium" data-testid="will-lose-credit">You will lose 1 credit.</div>
              )}
              {cancelPreview.will_count_noshow && (
                <div className="text-red-600 font-medium" data-testid="will-count-noshow">This will count as a no-show.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelPreview(null)} disabled={cancelBusy}>Keep booking</Button>
            <Button variant="destructive" onClick={confirmUnbook} disabled={cancelBusy} data-testid="confirm-unbook-btn">
              {cancelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V13.9 — Click-through session detail */}
      <SessionDetailModal
        session={detailSession}
        open={!!detailSession}
        onClose={() => setDetailSession(null)}
        onUpdate={fetchData}
      />

      {/* EDD mandatory for Elite users — forces EDD entry before booking */}
      <PregnancyOnboardingModal
        open={eddModalOpen && !user?.needs_phone_verification}
        onClose={() => { setEddModalOpen(false); if (refreshUser) refreshUser(); }}
        forceMode={needsEdd}
      />
    </div>
  );
}
